import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Readable, grouped resources:
 * - general: common UI strings
 * - auth: login/register bits
 * - editor: toolbar + common actions in editor surfaces
 * - modals: insert image/link/file dialogs
 * - invitations: accept-invite flow
 * - errors: error messages (incl. 403)
 * - faq: the FAQ page content
 *
 * Usage:
 *   t('auth.logIn')
 *   t('general.loading')
 *   t('invitations.acceptInvitation')
 */

const resources = {
  en: {
    translation: {
      general: {
        welcome: "Welcome to your Admin Panel",
        service:
          "You're able to customize your website. If something is not working please contact customer support.",
        menu: "Menu",
        customerService: "Customer Service",
        loading: "Loading...",
        lastUpdated: "Last updated: {{date}}",
        close: "Close",
        cancel: "Cancel",
        back: "Back"
      },
      header: {
        edit: "Edit",
        settings: "Settings"
      },
      footer: {
        version: "CMS {{version}} · Admin",
        tagline: "A compact CMS focused on speed, simplicity and collaboration.",
        sections: {
          support: "Support",
          legal: "Legal",
          resources: "Resources"
        },
        links: {
          website: "vbwebb.se →",
          faq: "FAQ",
          cookieSettings: "Cookie settings",
          gdpr: "GDPR",
          privacy: "Privacy Policy",
          terms: "Terms of Service",
          cookies: "Cookie Policy",
          status: "Status",
          changelog: "Changelog",
          guides: "Guides"
        },
        copyright: "All rights reserved."
      },
      consent: {
        message: "We use cookies to improve your experience. You can change your choices anytime.",
        accept: "Accept",
        manage: "Manage settings"
      },
      auth: {
        adminPanel: "Admin Panel",
        logIn: "Log in",
        register: "Register",
        logOut: "Log out",
        username: "Username",
        email: "Email",
        enterUsername: "Enter your username",
        password: "Password",
        enterPassword: "Enter your password",
        stayLoggedIn: "Stay logged in",
        loginFailed: "Login failed",
        registrationFailed: "Registration failed",
        loggingIn: "Logging in…",
        loginAndAccept: "Login & Accept",
        usePanel: "Try the CMS Panel",
        noAuth: "Not authenticated",
        noAccess: "No access to this resource",
      },

      editor: {
        createNew: "Create New",
        name: "Name",
        change: "Change",
        save: "Save",
        remove: "Remove",
        failedToLoad: "Failed to load",
        sectionNotFound: "Section not found",
        itemNotFound: "Item not found",
        toolbar: {
          bold: "Bold",
          underline: "Underline",
          italic: "Italic",
          link: "Link",
          image: "Image",
          file: "File",
          save: "Save"
        },
        defaultItemName: "Untitled",
        sections: "Sections",
        noSections: "No sections for this website.",
        selectSection: "Select a section",
        editWebsiteWith: "Edit website – {{name}}",
        leftMenuPrompt: "Select a section in the left menu.",
        newItem: "New item",
        value: "Value",

        table: {
          name: "Name",
          link: "Link",
          updated: "Updated",
          actions: "Actions"
        },

        actions: {
          edit: "Edit",
          delete: "Delete",
          removing: "Removing…",
          save: "Save",
          create: "Create",
          creating: "Creating…"
        },

        empty: {
          noItems: "No items."
        },

        confirmDelete: "Delete this item?",
        createIn: "Create in \"{{name}}\"",
        hint: {
          addWithNew: "Add items with the “+ New item” button above."
        },
        modal: {
          defaultTitle: "Create new item",
          nameLabel: "Name",
          namePlaceholder: "My new item"
        },
      },

      publicHeaderSimple: {
        login: "Login"
      },
      cookieSettings: {
        title: "Cookie Settings",
        updated: "Last updated: {{date}}",
        intro: "Choose which categories of cookies we may use. Necessary cookies are always on to keep the site working.",
        categories: {
          necessary: {
            title: "Strictly necessary",
            desc: "Required for basic functionality such as authentication and security."
          },
          preferences: {
            title: "Preferences",
            desc: "Remember choices like language and layout."
          },
          analytics: {
            title: "Analytics",
            desc: "Help us understand usage to improve the service."
          },
          media: {
            title: "Media",
            desc: "Enable embedded content or media services."
          }
        },
        actions: {
          reset: "Reset to defaults",
          save: "Save preferences",
          saving: "Saving…",
          acceptAll: "Accept all"
        },
        toast: {
          saved: "Your cookie preferences have been saved.",
          reset: "Preferences reset to defaults."
        }
      },

      websiteNotLoaded: "Website not loaded.",
      settings: {
        title: "Settings",
        inviteAdmins: {
          title: "Invite admins",
          subtitle: "Only admin invites are allowed right now.",
          pendingTitle: "Pending invitations"
        }
      },
      form: {
        emailLabel: "Email",
        emailPlaceholder: "user@example.com",
        sending: "Sending…",
        sendInvite: "Send admin invite",
        emailRequired: "Email is required"
      },
      feedback: {
        invitationSent: "Admin invitation sent",
        failedToSendInvite: "Failed to send invite",
        failedToLoadInvites: "Failed to load invitations",
        invitationResent: "Invitation resent",
        failedToResend: "Failed to resend",
        invitationRevoked: "Invitation revoked",
        failedToRevoke: "Failed to revoke"
      },
      table: {
        email: "Email",
        status: "Status",
        sent: "Sent",
        actions: "Actions"
      },
      emailStatus: {
        accepted: "Accepted",
        revoked: "Revoked",
        pending: "Pending"
      },
      actions: {
        resend: "Resend",
        revoke: "Revoke"
      },
      empty: {
        noPendingInvites: "No pending invites."
      },
      loading: "Loading…",


      modals: {
        cancel: "Cancel",
        image: {
          title: "Insert Image",
          uploadLabel: "Upload Image:",
        },
        link: {
          title: "Insert Link",
          displayText: "Display Text:",
          targetUrl: "Target URL:",
          insert: "Insert Link",
        },
        file: {
          title: "Insert File",
          displayText: "Display Text:",
          uploadLabel: "File Upload:",
          insert: "Insert File",
        }
      },

      invitations: {
        inviteNotFound: "Invite not found",
        youAreInvited: "You are invited",
        invitationFor: "Invitation for",
        adminAccessTo: "Admin access to {{title}} ({{name}})",
        loggedInAsNotice:
          "You’re currently logged in as {{username}}. To accept this invitation, your account’s email must be {{email}}.",
        acceptInvitation: "Accept invitation",
        missingToken: "Missing token",
        invalidOrExpiredInvite: "Invalid or expired invite",
        failedToAcceptInvite: "Failed to accept invitation",
        creating: "Creating…",
        createAccountAndAccept: "Create account & Accept",
        noteEmailMustMatch: "Note: your account’s email must be {{email}} to accept."
      },

      errors: {
        error403: "403 Forbidden - You don't have access to this resource",
        notFound: "Not Found",
        notLoaded: "Could not load data",
        failedToLoadSections: "Failed to load sections",
        failedToLoadItems: "Failed to load items",
        deleteFailed: "Delete failed",
        missingSection: "Missing section",
        nameRequired: "Name is required",
        failedToCreateItem: "Failed to create item",
        fileTooLargeImage: "Image is too large (max 5 MB).",
        fileTooLargeFile: "File is too large (max 10 MB).",
        uploadFailed: "Upload failed",
        saveFailed: "Save failed",
      },
      success: {
        uploadReady: "Upload ready to insert",
        itemUpdated: "Item updated",
        itemCreated: "Item added"
      },

      faq: {
        title: "Frequently Asked Questions",
        inviteAdmins: {
          title: "How do I invite admins?",
          body:
            "Go to Settings → Invitations, enter the email address, and send the invite. " +
            "The user can accept via the email link, then log in or create an account with that email."
        },
        addContent: {
          title: "How do I add content?",
          body:
            "Open Editor, choose a section, then create or edit items. " +
            "Some sections open a rich text editor directly; others show a list or a double-input form."
        },
        files: {
          title: "Where are files stored?",
          body:
            "Files are uploaded to an S3-compatible storage with secure, time-limited URLs. " +
            "Public links are optional depending on your bucket settings."
        }
      },
      legal: {
        gdpr: {
          fallbackSite: "Your Website",
          title: "GDPR (Data Protection)",
          lastUpdated: "Last updated: {{date}}",
          intro: "This policy explains how {{site}} processes personal data under the EU General Data Protection Regulation (GDPR).",

          controller: {
            title: "Data Controller",
            body: "{{site}} is the data controller. For questions, contact us at"
          },

          collect: {
            title: "What data we collect",
            items: {
              account: "Account details (name, email, login info).",
              content: "Content you create or upload.",
              tech: "Technical info (IP address, browser, logs)."
            }
          },

          purposes: {
            title: "Purposes and legal bases",
            items: {
              provide: "Provide and improve the service (contract/legitimate interest).",
              support: "Contact you for support (consent/contract).",
              security: "Security and abuse prevention (legitimate interest/legal obligation)."
            }
          },

          retention: {
            title: "Retention",
            body: "We store data as long as needed for the purposes above or as required by law. You may request deletion—see your rights below."
          },

          recipients: {
            title: "Recipients",
            body: "We may use trusted processors (hosting, email, storage). Data is not transferred outside the EU/EEA without appropriate safeguards."
          },

          rights: {
            title: "Your rights",
            items: {
              access: "Access to your data.",
              rectification: "Rectification and deletion (“right to be forgotten”).",
              erasure: "Deletion of your data where applicable.",
              restriction: "Restriction of processing.",
              objection: "Objection to processing.",
              portability: "Data portability.",
              consent: "Withdraw consent at any time.",
              complaint: "File a complaint with your supervisory authority (e.g., IMY in Sweden)."
            },
            cta: "Contact us to exercise your rights at"
          }
        },
        cookies: {
          fallbackSite: "Your Website",
          title: "Cookie Policy",
          lastUpdated: "Last updated: {{date}}",
          intro: "{{site}} uses cookies and similar technologies to operate and improve the service.",

          what: {
            title: "What are cookies?",
            body: "Small text files stored on your device to remember preferences, keep you signed in, and measure performance."
          },

          categories: {
            title: "Categories",
            items: {
              strict: "Strictly necessary",
              prefs: "Preferences",
              analytics: "Analytics",
              media: "Media"
            },
            desc: {
              strict: "(e.g., session/auth).",
              prefs: "(e.g., language).",
              analytics: "(usage statistics).",
              media: "(e.g., embedded content)."
            }
          },

          manage: {
            title: "Managing cookies",
            body: "You can manage cookies in your browser settings. You can also adjust your preferences here:",
            open: "Open cookie settings"
          }
        },
        privacy: {
          fallbackSite: "Your Website",
          title: "Privacy Policy",
          lastUpdated: "Last updated: {{date}}",
          intro: "This Privacy Policy describes how {{site}} collects, uses, and shares your information.",

          collect: {
            title: "Information We Collect",
            items: {
              account: "Account and contact info (name, email).",
              content: "Content you create/upload.",
              usage: "Usage & device data (logs, IP, browser).",
              cookies: "Cookies (see Cookie Policy)."
            }
          },

          use: {
            title: "How We Use Information",
            items: {
              provide: "Provide and improve the service.",
              communicate: "Communicate with you (support, updates).",
              security: "Security, fraud prevention, and compliance."
            }
          },

          sharing: {
            title: "Sharing",
            body: "We may share data with processors (hosting, email, storage) bound by data protection agreements. We do not sell personal data."
          },

          transfers: {
            title: "International Transfers",
            body: "If data is transferred outside the EU/EEA, appropriate safeguards (e.g., SCCs) are applied."
          },

          contact: {
            title: "Contact",
            body: "Questions? Email"
          }
        },
        terms: {
          fallbackSite: "Your Website",
          title: "Terms of Service",
          lastUpdated: "Last updated: {{date}}",

          agreement: {
            title: "1. Agreement",
            body: "By accessing or using {{site}}, you agree to these Terms."
          },

          accounts: {
            title: "2. Accounts",
            body: "You’re responsible for your account credentials and content. Notify us immediately of any unauthorized use."
          },

          acceptable: {
            title: "3. Acceptable Use",
            items: {
              item1: "No unlawful, infringing, or harmful content.",
              item2: "No abuse of the platform (spam, exploits, DoS).",
              item3: "Respect third-party rights and applicable laws."
            }
          },

          ownership: {
            title: "4. Content Ownership",
            body: "You retain rights to your content. You grant {{site}} a limited license to store and process it to provide the service."
          },

          changes: {
            title: "5. Service Changes",
            body: "We may modify or discontinue features with reasonable notice where required by law."
          },

          disclaimer: {
            title: "6. Disclaimer & Liability",
            body: "The service is provided “as is” to the extent permitted by law. We are not liable for indirect or consequential damages."
          },

          termination: {
            title: "7. Termination",
            body: "We may suspend or terminate access for violations of these Terms. You may stop using the service at any time."
          },

          law: {
            title: "8. Governing Law",
            body: "These Terms are governed by the laws of Sweden, unless mandatory law states otherwise."
          }
        }
      },
      status: {
        title: "Status",
        updated: "Last updated: {{date}}",
        intro: "Current status of core services. If you notice an issue not listed here, please contact support.",
        services: {
          api: "API",
          database: "Database",
          storage: "File Storage",
          email: "Email Delivery"
        },
        notes: {
          ok: "Operational"
        },
        incidentsBlurb: "Report incidents or outages to"
      },

      changelog: {
        title: "Changelog",
        subtitle: "Highlights of recent releases.",
        version: "Version {{version}}",
        items: {
          // 1.1.2
          "112_1": "Public Cookie Settings page added.",
          "112_2": "Improved admin footer with legal links.",
          "112_3": "Editor error messages translated and styled.",
          // 1.1.1
          "111_1": "Section slugs are now used in editor routes.",
          "111_2": "Double Input Form updated to match List Editor UI.",
          // 1.1.0
          "110_1": "Invitations flow for admins (accept via email).",
          "110_2": "New Admin Shell with per-site context."
        }
      },

      guides: {
        title: "Guides",
        subtitle: "Short how-tos for common tasks.",
        cards: {
          invite: {
            title: "Invite admins",
            desc: "Send an invite by email and let users accept with the same address."
          },
          editor: {
            title: "Editing content",
            desc: "Use List Editor, Double Input, or the single Editor depending on section type."
          },
          files: {
            title: "Uploading files",
            desc: "Upload images and documents to S3-compatible storage."
          },
          cookies: {
            title: "Cookie settings",
            desc: "Control analytics, media and preference cookies."
          }
        },
        moreHelp: "Need more help? Contact"
      }

    }
  },

  se: {
    translation: {
      general: {
        welcome: "Välkommen till eran Administratör Panel",
        service:
          "Här kan du hantera innehållet på eran hemsida. Om något inte fungerar kontakta kundservice.",
        menu: "Meny",
        customerService: "Kundservice",
        loading: "Laddar...",
        lastUpdated: "Senast uppdaterad: {{date}}",
        close: "Stäng",
        cancel: "Avbryt",
        back: "Tillbaka"
      },
      header: {
        edit: "Ändra",
        settings: "Inställningar"
      },

      footer: {
        version: "CMS {{version}} · Admin",
        tagline: "Ett kompakt CMS med fokus på snabbhet, enkelhet och samarbete.",
        sections: {
          support: "Support",
          legal: "Juridik",
          resources: "Resurser"
        },
        links: {
          website: "vbwebb.se →",
          faq: "FAQ",
          cookieSettings: "Cookie-inställningar",
          gdpr: "GDPR",
          privacy: "Integritetspolicy",
          terms: "Användarvillkor",
          cookies: "Cookiepolicy",
          status: "Status",
          changelog: "Ändringslogg",
          guides: "Guider"
        },
        copyright: "Alla rättigheter förbehållna."
      },

      consent: {
        message: "Vi använder cookies för att förbättra din upplevelse. Genom att fortsätta använda denna webbplats godkänner du vår användning av cookies.",
        accept: "Godkänn",
        manage: "Hantera inställningar"
      },
      auth: {
        adminPanel: "Administratör Panel",
        logIn: "Logga in",
        register: "Registrera",
        logOut: "Logga ut",
        username: "Användarnamn",
        email: "E-post",
        enterUsername: "Ange ditt användarnamn",
        password: "Lösenord",
        enterPassword: "Ange ditt lösenord",
        stayLoggedIn: "Håll mig inloggad",
        loginFailed: "Inloggning misslyckades",
        registrationFailed: "Registrering misslyckades",
        loggingIn: "Loggar in…",
        loginAndAccept: "Logga in & Acceptera",
        usePanel: "Testa CMS Panelen",
        noAuth: "Du saknar tillgång",
        noAccess: "Ingen tillgång till denna resurs",
      },

      editor: {
        createNew: "Skapa ny",
        name: "Namn",
        change: "Ändra",
        save: "Spara",
        remove: "Ta bort",
        failedToLoad: "Kunde inte ladda",
        sectionNotFound: "Sektion hittades inte",
        itemNotFound: "Post hittades inte",
        toolbar: {
          bold: "Fet",
          underline: "Understrykning",
          italic: "Kursiv",
          link: "Länk",
          image: "Bild",
          file: "Fil",
          save: "Spara"
        },
        defaultItemName: "Namnlös",
        sections: "Sektioner",
        noSections: "Inga sektioner för denna webbplats.",
        selectSection: "Välj en sektion",
        editWebsiteWith: "Redigera webbplats – {{name}}",
        leftMenuPrompt: "Välj en sektion i menyn till vänster.",
        newItem: "Ny post",
        value: "Värde",

        table: {
          name: "Namn",
          link: "Länk",
          updated: "Uppdaterad",
          actions: "Åtgärder"
        },

        actions: {
          edit: "Redigera",
          delete: "Ta bort",
          save: "Spara",
          removing: "Tar bort…"
        },

        empty: {
          noItems: "Inga poster."
        },

        confirmDelete: "Ta bort den här posten?",
        createIn: "Skapa i \"{{name}}\"",
        hint: {
          addWithNew: "Lägg till poster med knappen ”+ Ny post” ovan."
        },
        modal: {
          defaultTitle: "Skapa ny post",
          nameLabel: "Namn",
          namePlaceholder: "Min nya post"
        },
        actions: {
          edit: "Redigera",
          delete: "Ta bort",
          removing: "Tar bort…",
          create: "Skapa",
          creating: "Skapar…"
        }
      },
      publicHeaderSimple: {
        login: "Logga in"
      },
      cookieSettings: {
        title: "Cookie-inställningar",
        updated: "Senast uppdaterad: {{date}}",
        intro: "Välj vilka kategorier av cookies vi får använda. Nödvändiga cookies är alltid aktiva för att sidan ska fungera.",
        categories: {
          necessary: {
            title: "Nödvändiga",
            desc: "Krävs för grundläggande funktioner som inloggning och säkerhet."
          },
          preferences: {
            title: "Preferenser",
            desc: "Kommer ihåg val som språk och layout."
          },
          analytics: {
            title: "Analys",
            desc: "Hjälper oss förstå användning för att förbättra tjänsten."
          },
          media: {
            title: "Media",
            desc: "Aktiverar inbäddat innehåll och medietjänster."
          }
        },
        actions: {
          reset: "Återställ standard",
          save: "Spara inställningar",
          saving: "Sparar…",
          acceptAll: "Acceptera alla"
        },
        toast: {
          saved: "Dina cookie-inställningar har sparats.",
          reset: "Inställningarna återställdes."
        }
      },

      websiteNotLoaded: "Webbplatsen är inte laddad.",
      settings: {
        title: "Inställningar",
        inviteAdmins: {
          title: "Bjud in administratörer",
          subtitle: "Just nu är endast admin-inbjudningar tillåtna.",
          pendingTitle: "Väntande inbjudningar"
        }
      },
      form: {
        emailLabel: "E-post",
        emailPlaceholder: "anvandare@exempel.se",
        sending: "Skickar…",
        sendInvite: "Skicka admin-inbjudan",
        emailRequired: "E-post krävs"
      },
      feedback: {
        invitationSent: "Admin-inbjudan skickad",
        failedToSendInvite: "Misslyckades med att skicka inbjudan",
        failedToLoadInvites: "Misslyckades med att läsa in inbjudningar",
        invitationResent: "Inbjudan skickades igen",
        failedToResend: "Misslyckades med att skicka igen",
        invitationRevoked: "Inbjudan återkallad",
        failedToRevoke: "Misslyckades med att återkalla"
      },
      table: {
        email: "E-post",
        status: "Status",
        sent: "Skickad",
        actions: "Åtgärder"
      },
      emailStatus: {
        accepted: "Accepterad",
        revoked: "Återkallad",
        pending: "Väntar"
      },
      actions: {
        resend: "Skicka igen",
        revoke: "Återkalla"
      },
      empty: {
        noPendingInvites: "Inga väntande inbjudningar."
      },
      loading: "Laddar…",

        
      modals: {
        cancel: "Avbryt",
        image: {
          title: "Infoga bild",
          uploadLabel: "Ladda upp bild:",
        },
        link: {
          title: "Infoga länk",
          displayText: "Visningstext:",
          targetUrl: "Mål-URL:",
          insert: "Infoga länk",
        },
        file: {
          title: "Infoga fil",
          displayText: "Visningstext:",
          uploadLabel: "Filuppladdning:",
          insert: "Infoga fil",
        }
      },

      invitations: {
        inviteNotFound: "Inbjudan hittades inte",
        youAreInvited: "Du är inbjuden",
        invitationFor: "Inbjudan för",
        adminAccessTo: "Administratörs åtkomst till {{title}} ({{name}})",
        loggedInAsNotice:
          "Du är inloggad som {{username}}. För att acceptera denna inbjudan måste din kontos e-post vara {{email}}.",
        acceptInvitation: "Acceptera inbjudan",
        missingToken: "Saknar token",
        invalidOrExpiredInvite: "Ogiltig eller utgången inbjudan",
        failedToAcceptInvite: "Misslyckades med att acceptera inbjudan",
        creating: "Skapar…",
        createAccountAndAccept: "Skapa konto & Acceptera",
        noteEmailMustMatch: "Notera: din kontos e-post måste vara {{email}} för att acceptera."
      },

      errors: {
        error403: "403 Förbjudet - Du har inte tillgång till denna resurs",
        notFound: "Hittades inte",
        notLoaded: "Kunde inte ladda data",
        failedToLoadSections: "Misslyckades att ladda sektioner",
        failedToLoadItems: "Misslyckades att ladda poster",
        deleteFailed: "Borttagning misslyckades",
        missingSection: "Saknar sektion",
        nameRequired: "Namn krävs",
        failedToCreateItem: "Misslyckades att skapa post",
        fileTooLargeImage: "Bilden är för stor (max 5 MB).",
        fileTooLargeFile: "Filen är för stor (max 10 MB).",
        uploadFailed: "Uppladdning misslyckades",
        saveFailed: "Sparande misslyckades",
        missingSection: "Saknar sektion"
      },
      success: {
        uploadReady: "Uppladdning redo att infogas",
        itemUpdated: "Post uppdaterad",
        itemCreated: "Post tillagd"
      },

      faq: {
        title: "Vanliga frågor och svar",
        inviteAdmins: {
          title: "Hur bjuder jag in administratörer?",
          body:
            "Gå till Inställningar → Inbjudningar, skriv in e-postadressen och skicka inbjudan. " +
            "Användaren kan acceptera via länken i mejlet och sedan logga in eller skapa ett konto med den e-posten."
        },
        addContent: {
          title: "Hur lägger jag till innehåll?",
          body:
            "Öppna Redigera, välj en sektion och skapa eller redigera poster. " +
            "Vissa sektioner öppnar en rik text-editor direkt; andra visar en lista eller ett formulär med två fält."
        },
        files: {
          title: "Var lagras filer?",
          body:
            "Filer laddas upp till ett S3-kompatibelt lagringskonto med säkra, tidsbegränsade länkar. " +
            "Offentliga länkar är valfria beroende på dina bucket-inställningar."
        }
      },
      legal: {
        gdpr: {
          fallbackSite: "Er webbplats",
          title: "GDPR (Dataskydd)",
          lastUpdated: "Senast uppdaterad: {{date}}",
          intro: "Denna policy förklarar hur {{site}} behandlar personuppgifter enligt EU:s dataskyddsförordning (GDPR).",

          controller: {
            title: "Personuppgiftsansvarig",
            body: "{{site}} är personuppgiftsansvarig. Vid frågor, kontakta oss på"
          },

          collect: {
            title: "Vilka uppgifter vi samlar in",
            items: {
              account: "Kontouppgifter (namn, e-post, inloggningsuppgifter).",
              content: "Innehåll som du själv skapar eller laddar upp.",
              tech: "Teknisk information (IP-adress, webbläsare, loggar)."
            }
          },

          purposes: {
            title: "Ändamål och laglig grund",
            items: {
              provide: "Tillhandahålla och förbättra tjänsten (avtal/intresseavvägning).",
              support: "Kontakta dig vid support (samtycke/avtal).",
              security: "Säkerhet och missbruksförebyggande (intresseavvägning/rättslig förpliktelse)."
            }
          },

          retention: {
            title: "Lagringstider",
            body: "Vi sparar uppgifter så länge det krävs för ovan angivna ändamål eller enligt lag. Du kan begära radering – se dina rättigheter nedan."
          },

          recipients: {
            title: "Mottagare",
            body: "Vi kan använda betrodda personuppgiftsbiträden (hosting, e-post, lagring). Personuppgifter överförs inte utanför EU/EES utan lämpliga skyddsåtgärder."
          },

          rights: {
            title: "Dina rättigheter",
            items: {
              access: "Tillgång till dina uppgifter.",
              rectification: "Rättning och radering (”rätten att bli bortglömd”).",
              erasure: "Radering av dina uppgifter där det är tillämpligt.",
              restriction: "Begränsning av behandling.",
              objection: "Invändning mot behandling.",
              portability: "Dataportabilitet.",
              consent: "Återkalla samtycke när som helst.",
              complaint: "Lämna klagomål till tillsynsmyndighet (t.ex. IMY i Sverige)."
            },
            cta: "Kontakta oss för att utöva dina rättigheter på"
          }
        },
        cookies: {
          fallbackSite: "Er webbplats",
          title: "Cookiepolicy",
          lastUpdated: "Senast uppdaterad: {{date}}",
          intro: "{{site}} använder cookies och liknande tekniker för att driva och förbättra tjänsten.",

          what: {
            title: "Vad är cookies?",
            body: "Små textfiler som lagras på din enhet för att komma ihåg inställningar, hålla dig inloggad och mäta prestanda."
          },

          categories: {
            title: "Kategorier",
            items: {
              strict: "Absolut nödvändiga",
              prefs: "Preferenser",
              analytics: "Analys",
              media: "Media"
            },
            desc: {
              strict: "(t.ex. session/inloggning).",
              prefs: "(t.ex. språk).",
              analytics: "(användningsstatistik).",
              media: "(t.ex. inbäddat innehåll)."
            }
          },

          manage: {
            title: "Hantera cookies",
            body: "Du kan hantera cookies i din webbläsares inställningar. Du kan också justera dina preferenser här:",
            open: "Öppna cookie-inställningar"
          }
        },
        privacy: {
          fallbackSite: "Er webbplats",
          title: "Integritetspolicy",
          lastUpdated: "Senast uppdaterad: {{date}}",
          intro: "Denna integritetspolicy beskriver hur {{site}} samlar in, använder och delar din information.",

          collect: {
            title: "Vilken information vi samlar in",
            items: {
              account: "Konto- och kontaktuppgifter (namn, e-post).",
              content: "Innehåll som du skapar/laddar upp.",
              usage: "Användnings- och enhetsdata (loggar, IP, webbläsare).",
              cookies: "Cookies (se Cookiepolicy)."
            }
          },

          use: {
            title: "Hur vi använder information",
            items: {
              provide: "Tillhandahålla och förbättra tjänsten.",
              communicate: "Kommunicera med dig (support, uppdateringar).",
              security: "Säkerhet, bedrägeriförebyggande och regelefterlevnad."
            }
          },

          sharing: {
            title: "Delning",
            body: "Vi kan dela data med personuppgiftsbiträden (hosting, e-post, lagring) bundna av dataskyddsavtal. Vi säljer inte personuppgifter."
          },

          transfers: {
            title: "Internationella överföringar",
            body: "Om data överförs utanför EU/EES tillämpas lämpliga skyddsåtgärder (t.ex. SCC:er)."
          },

          contact: {
            title: "Kontakt",
            body: "Frågor? Mejla"
          }
        },
        terms: {
          fallbackSite: "Er webbplats",
          title: "Användarvillkor",
          lastUpdated: "Senast uppdaterad: {{date}}",

          agreement: {
            title: "1. Avtal",
            body: "Genom att använda {{site}} godkänner du dessa villkor."
          },

          accounts: {
            title: "2. Konton",
            body: "Du är ansvarig för dina kontouppgifter och ditt innehåll. Meddela oss omedelbart vid obehörig användning."
          },

          acceptable: {
            title: "3. Acceptabel användning",
            items: {
              item1: "Inget olagligt, intrångsgörande eller skadligt innehåll.",
              item2: "Inget missbruk av plattformen (spam, exploitering, DoS).",
              item3: "Respektera tredje parts rättigheter och gällande lagar."
            }
          },

          ownership: {
            title: "4. Äganderätt till innehåll",
            body: "Du behåller rättigheterna till ditt innehåll. Du ger {{site}} en begränsad licens att lagra och behandla det för att tillhandahålla tjänsten."
          },

          changes: {
            title: "5. Ändringar av tjänsten",
            body: "Vi kan ändra eller avveckla funktioner med skäligt förhandsbesked där lag kräver det."
          },

          disclaimer: {
            title: "6. Ansvarsbegränsning",
            body: "Tjänsten tillhandahålls i befintligt skick i den utsträckning lagen tillåter. Vi ansvarar inte för indirekta eller följdskador."
          },

          termination: {
            title: "7. Uppsägning",
            body: "Vi kan stänga av eller avsluta åtkomst vid brott mot villkoren. Du kan sluta använda tjänsten när som helst."
          },

          law: {
            title: "8. Tillämplig lag",
            body: "Villkoren regleras av svensk lag, om inte tvingande lag säger annat."
          }
        }
      },
      status: {
        title: "Status",
        updated: "Senast uppdaterad: {{date}}",
        intro: "Aktuell status för kärntjänster. Om du märker ett problem som inte listas här, kontakta support.",
        services: {
          api: "API",
          database: "Databas",
          storage: "Fillagring",
          email: "E-postutskick"
        },
        notes: {
          ok: "Tillgänglig"
        },
        incidentsBlurb: "Rapportera incidenter eller driftstopp till"
      },

      changelog: {
        title: "Ändringslogg",
        subtitle: "Höjdpunkter från senaste versionerna.",
        version: "Version {{version}}",
        items: {
          // 1.1.2
          "112_1": "Publik sida för cookie-inställningar tillagd.",
          "112_2": "Förbättrad admin-footer med juridiska länkar.",
          "112_3": "Översatta och snyggare felmeddelanden i editorn.",
          // 1.1.1
          "111_1": "Sektioners slugs används nu i editor-länkar.",
          "111_2": "Double Input Form uppdaterad för att matcha List Editor UI.",
          // 1.1.0
          "110_1": "Inbjudningsflöde för administratörer (acceptera via e-post).",
          "110_2": "Ny Admin Shell med webbplats-context."
        }
      },

      guides: {
        title: "Guider",
        subtitle: "Korta guider för vanliga uppgifter.",
        cards: {
          invite: {
            title: "Bjud in administratörer",
            desc: "Skicka en inbjudan via e-post och låt användaren acceptera med samma adress."
          },
          editor: {
            title: "Redigera innehåll",
            desc: "Använd List Editor, Double Input eller enkel Editor beroende på sektionstyp."
          },
          files: {
            title: "Ladda upp filer",
            desc: "Ladda upp bilder och dokument till S3-kompatibelt lagring."
          },
          cookies: {
            title: "Cookie-inställningar",
            desc: "Styr analys, media och preferens-cookies."
          }
        },
        moreHelp: "Behöver du mer hjälp? Kontakta"
      }

    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    debug: process.env.NODE_ENV !== 'production',
    fallbackLng: 'en',
    interpolation: { escapeValue: false }
  });

export default i18n;
